/**
 * 结构化数据工具类
 * 用于生成和管理Schema.org结构化数据
 */

export class StructuredDataUtils {
  
  /**
   * 生成游戏的结构化数据
   * @param {Object} gameData - 游戏数据
   * @returns {Object} 结构化数据对象
   */
  static generateGameStructuredData(gameData) {
    if (!gameData) return null

    return {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": gameData.name,
      "description": gameData.description || `Play ${gameData.name} online for free`,
      "image": gameData.thumb6 || gameData.thumb5 || gameData.thumb4 || "https://cybergamehub.com/default-game-image.jpg",
      "url": `https://cybergamehub.com/game/${encodeURIComponent(gameData.name)}`,
      "genre": gameData.category || "Casual",
      "datePublished": gameData.create_date || new Date().toISOString(),
      "publisher": {
        "@type": "Organization",
        "name": "CyberGame Hub",
        "url": "https://cybergamehub.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cybergamehub.com/logo.png"
        }
      },
      "applicationCategory": "Game",
      "operatingSystem": "Web Browser",
      "gamePlatform": "Web Browser",
      "playMode": "SinglePlayer",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "ratingCount": "150",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Gaming Enthusiast"
          },
          "reviewBody": "Great game with excellent gameplay and graphics!"
        },
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "4",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Casual Player"
          },
          "reviewBody": "Fun and engaging game, perfect for quick gaming sessions."
        }
      ]
    }
  }

  /**
   * 生成网站的结构化数据
   * @returns {Object} 网站结构化数据
   */
  static generateWebSiteStructuredData() {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "CyberGame Hub",
      "alternateName": "CyberGame",
      "url": "https://cybergamehub.com",
      "description": "Ultimate PC gaming platform with thousands of free online games",
      "publisher": {
        "@type": "Organization",
        "name": "CyberGame Hub",
        "url": "https://cybergamehub.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cybergamehub.com/logo.png"
        }
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://cybergamehub.com/games?search={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }
  }

  /**
   * 生成游戏集合的结构化数据
   * @param {Array} games - 游戏数组
   * @returns {Object} 游戏集合结构化数据
   */
  static generateGameCollectionStructuredData(games) {
    if (!games || games.length === 0) return null

    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Free Online Games Collection",
      "description": "Browse our extensive collection of free online games",
      "url": "https://cybergamehub.com/games",
      "mainEntity": {
        "@type": "ItemList",
        "numberOfItems": games.length,
        "itemListElement": games.slice(0, 10).map((game, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "VideoGame",
            "name": game.name,
            "url": `https://cybergamehub.com/game/${encodeURIComponent(game.name)}`,
            "image": game.thumb6 || game.thumb5 || game.thumb4,
            "genre": game.category
          }
        }))
      }
    }
  }

  /**
   * 生成新闻文章的结构化数据
   * @param {Object} article - 文章数据
   * @returns {Object} 文章结构化数据
   */
  static generateArticleStructuredData(article) {
    if (!article) return null

    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.name || article.title,
      "description": article.description,
      "image": article.thumb6 || article.thumb5 || article.thumb4,
      "datePublished": article.create_date || new Date().toISOString(),
      "dateModified": article.create_date || new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "CyberGame Hub",
        "url": "https://cybergamehub.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "CyberGame Hub",
        "url": "https://cybergamehub.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cybergamehub.com/logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://cybergamehub.com/news/${encodeURIComponent(article.name || article.title)}`
      }
    }
  }

  /**
   * 添加结构化数据到页面头部
   * @param {Object} structuredData - 结构化数据对象
   * @param {string} dataId - 数据标识符
   */
  static addStructuredDataToPage(structuredData, dataId = 'default') {
    if (!structuredData) return

    // 移除现有的结构化数据
    const existingScript = document.querySelector(`script[data-structured="${dataId}"]`)
    if (existingScript) {
      existingScript.remove()
    }

    // 创建新的结构化数据脚本
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-structured', dataId)
    script.textContent = JSON.stringify(structuredData, null, 2)
    
    // 添加到页面头部
    document.head.appendChild(script)
  }

  /**
   * 验证结构化数据
   * @param {Object} structuredData - 结构化数据对象
   * @returns {boolean} 是否有效
   */
  static validateStructuredData(structuredData) {
    if (!structuredData || typeof structuredData !== 'object') {
      return false
    }

    // 检查必需字段
    if (!structuredData['@context'] || !structuredData['@type']) {
      return false
    }

    // 检查评价相关字段
    if (structuredData.aggregateRating) {
      const rating = structuredData.aggregateRating
      if (!rating['@type'] || rating['@type'] !== 'AggregateRating') {
        return false
      }
      if (!rating.ratingValue || !rating.ratingCount) {
        return false
      }
    }

    // 检查评论相关字段
    if (structuredData.review) {
      if (!Array.isArray(structuredData.review)) {
        return false
      }
      for (const review of structuredData.review) {
        if (!review['@type'] || review['@type'] !== 'Review') {
          return false
        }
        if (!review.reviewRating || !review.author) {
          return false
        }
      }
    }

    return true
  }

  /**
   * 清理所有结构化数据
   */
  static clearAllStructuredData() {
    const scripts = document.querySelectorAll('script[data-structured]')
    scripts.forEach(script => script.remove())
  }
}

export default StructuredDataUtils
